import React, { useRef, useCallback } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, MessageSquare, Check, Activity, Zap, TrendingUp, Shield } from "lucide-react";

/* ──────────────────────────────────────────────────────────────
   NEURAL NETWORK SVG
────────────────────────────────────────────────────────────── */
const NN_NODES = [
  // outer ring
  {x:48, y:78},  {x:200,y:32}, {x:368,y:42}, {x:512,y:88},
  {x:555,y:240}, {x:515,y:395},{x:368,y:510},{x:178,y:505},
  {x:38, y:375},
  // mid ring
  {x:148,y:162},{x:308,y:124},{x:458,y:182},
  {x:488,y:358},{x:318,y:442},{x:128,y:345},
  // inner (near sphere at ~290,255)
  {x:232,y:202},{x:355,y:188},{x:408,y:288},{x:262,y:330},
];
const NN_EDGES = [
  [0,9],[1,9],[1,10],[2,10],[2,11],[3,11],[3,4],[4,12],
  [5,12],[5,13],[6,13],[7,14],[7,13],[8,14],[8,9],
  [9,15],[10,16],[10,15],[11,16],[11,17],[12,17],
  [13,18],[14,15],[14,18],
  [15,16],[16,17],[17,18],[18,15],
];
const ANIM_EDGES  = [0,2,5,8,11,15,19,23];
const ANIM_EDGES_R= [3,7,13,20];

function NeuralNet({ style }) {
  return (
    <svg width="580" height="530" viewBox="0 0 580 530"
      className="absolute inset-0 w-full h-full pointer-events-none" style={style}>
      <defs>
        <radialGradient id="ng"><stop offset="0%" stopColor="#93c5fd"/><stop offset="100%" stopColor="#3b82f6"/></radialGradient>
        <filter id="nglow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2.5" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        <filter id="dglow" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation="1.8" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>

      {/* Static edges */}
      {NN_EDGES.map(([a,b],i)=>(
        <line key={i}
          x1={NN_NODES[a].x} y1={NN_NODES[a].y}
          x2={NN_NODES[b].x} y2={NN_NODES[b].y}
          stroke={`rgba(59,130,246,${i%4===0?0.22:i%3===0?0.15:0.09})`}
          strokeWidth={i%5===0?0.7:0.4}
        />
      ))}

      {/* Forward animated dots */}
      {ANIM_EDGES.map((ei,i)=>{
        const [a,b]=NN_EDGES[ei]||[0,1];
        return (
          <circle key={`f${i}`} r="2.2" fill="rgba(96,165,250,0.95)" filter="url(#dglow)">
            <animateMotion path={`M${NN_NODES[a].x},${NN_NODES[a].y} L${NN_NODES[b].x},${NN_NODES[b].y}`}
              dur={`${2.2+i*0.65}s`} begin={`${i*0.55}s`} repeatCount="indefinite"/>
          </circle>
        );
      })}

      {/* Reverse animated dots (violet) */}
      {ANIM_EDGES_R.map((ei,i)=>{
        const [a,b]=NN_EDGES[ei]||[0,1];
        return (
          <circle key={`r${i}`} r="1.7" fill="rgba(167,139,250,0.9)" filter="url(#dglow)">
            <animateMotion path={`M${NN_NODES[b].x},${NN_NODES[b].y} L${NN_NODES[a].x},${NN_NODES[a].y}`}
              dur={`${3.2+i*0.8}s`} begin={`${i*0.9+1.2}s`} repeatCount="indefinite"/>
          </circle>
        );
      })}

      {/* Nodes */}
      {NN_NODES.map((n,i)=>{
        const inner=i>=15, mid=i>=9&&i<15;
        const r=inner?4.5:mid?3.2:2.2;
        const op=inner?0.95:mid?0.7:0.5;
        return (
          <circle key={i} cx={n.x} cy={n.y} r={r}
            fill="url(#ng)" opacity={op}
            filter={inner?"url(#nglow)":undefined}
          >
            {inner&&<animate attributeName="opacity" values={`${op};1;${op}`}
              dur={`${1.8+i*0.4}s`} repeatCount="indefinite"/>}
          </circle>
        );
      })}
    </svg>
  );
}

/* ──────────────────────────────────────────────────────────────
   CENTRAL AI SPHERE
────────────────────────────────────────────────────────────── */
function AISphere() {
  return (
    <div className="relative" style={{width:176,height:176}}>
      {/* Outer ambient glow */}
      <div className="absolute rounded-full pointer-events-none"
        style={{inset:'-60px',background:'radial-gradient(circle,rgba(59,130,246,0.14) 0%,rgba(139,92,246,0.07) 45%,transparent 70%)',filter:'blur(28px)',animation:'glow-orb 4s ease-in-out infinite'}}/>

      {/* Orbit ring 1 */}
      <div className="absolute rounded-full" style={{
        width:248,height:248,top:'50%',left:'50%',
        border:'1px solid rgba(96,165,250,0.32)',
        boxShadow:'0 0 10px rgba(96,165,250,0.12)',
        transform:'translate(-50%,-50%) perspective(600px) rotateX(73deg)',
      }}/>

      {/* Orbit ring 2 */}
      <div className="absolute rounded-full" style={{
        width:308,height:308,top:'50%',left:'50%',
        border:'1px solid rgba(167,139,250,0.2)',
        transform:'translate(-50%,-50%) perspective(600px) rotateX(66deg) rotateZ(55deg)',
      }}/>

      {/* Orbit ring 3 (thin outer) */}
      <div className="absolute rounded-full" style={{
        width:360,height:360,top:'50%',left:'50%',
        border:'0.5px solid rgba(59,130,246,0.1)',
        transform:'translate(-50%,-50%) perspective(600px) rotateX(80deg) rotateZ(20deg)',
      }}/>

      {/* Sphere body */}
      <div className="relative w-full h-full rounded-full overflow-hidden" style={{
        background:`radial-gradient(circle at 34% 30%, rgba(147,197,253,0.95) 0%, rgba(99,102,241,0.82) 18%, rgba(59,130,246,0.72) 38%, rgba(29,78,216,0.88) 62%, rgba(7,12,38,0.98) 88%)`,
        boxShadow:`0 0 32px rgba(59,130,246,0.58),0 0 72px rgba(59,130,246,0.32),0 0 130px rgba(139,92,246,0.22),inset 0 0 22px rgba(255,255,255,0.08),inset -14px -14px 32px rgba(0,0,0,0.6)`,
        animation:'sphere-pulse 3.5s ease-in-out infinite',
      }}>
        {/* Specular highlight */}
        <div className="absolute rounded-full" style={{width:52,height:36,background:'radial-gradient(ellipse,rgba(255,255,255,0.38) 0%,transparent 70%)',top:'17%',left:'20%',filter:'blur(4px)'}}/>
        {/* Scan line */}
        <div className="absolute left-0 right-0 h-px opacity-20"
          style={{background:'linear-gradient(90deg,transparent,rgba(147,197,253,0.8),transparent)',animation:'scan-line 4s linear infinite'}}/>
      </div>

      {/* Satellite dots */}
      <div className="absolute w-3 h-3 rounded-full" style={{top:'50%',left:'50%',
        background:'rgba(96,165,250,1)',boxShadow:'0 0 8px rgba(96,165,250,0.95),0 0 18px rgba(96,165,250,0.5)',
        animation:'satellite-a 8s linear infinite'}}/>
      <div className="absolute w-2 h-2 rounded-full" style={{top:'50%',left:'50%',
        background:'rgba(167,139,250,1)',boxShadow:'0 0 7px rgba(167,139,250,0.9)',
        animation:'satellite-b 13s linear 3s infinite'}}/>
      <div className="absolute w-1.5 h-1.5 rounded-full" style={{top:'50%',left:'50%',
        background:'rgba(52,211,153,1)',boxShadow:'0 0 5px rgba(52,211,153,0.9)',
        animation:'satellite-c 10s linear 6s infinite'}}/>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   FLOATING CARDS
────────────────────────────────────────────────────────────── */
function glassCard(extra="") {
  return `absolute rounded-2xl border border-white/[0.09] bg-[#0C0C10]/85 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.06)] ${extra}`;
}

function AutomationCard() {
  const items = [
    {label:"WhatsApp Bot",  active:true},
    {label:"CRM Sync",     active:true},
    {label:"Lead Engine",  active:true},
    {label:"Voice Agent",  active:false},
  ];
  return (
    <div className={glassCard("p-4 w-52")} style={{border:"1px solid rgba(59,130,246,0.15)"}}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] font-mono text-white/35 tracking-widest uppercase">Automations</span>
        <span className="flex items-center gap-1 text-[9.5px] text-emerald-400">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"/>Live
        </span>
      </div>
      <div className="space-y-1.5">
        {items.map(it=>(
          <div key={it.label} className="flex items-center justify-between">
            <span className="text-[11px] text-white/55">{it.label}</span>
            <div className={`w-6 h-3 rounded-full flex items-center px-0.5 transition-all ${it.active?'bg-blue-500/80 justify-end':'bg-white/[0.08]'}`}>
              <div className="w-2 h-2 rounded-full bg-white"/>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 pt-3 border-t border-white/[0.05] flex items-center justify-between">
        <span className="text-[10px] text-white/30">Running</span>
        <span className="text-[13px] font-bold text-blue-400">3 / 4</span>
      </div>
    </div>
  );
}

function MetricsCard() {
  const bars=[22,40,35,58,48,70,82,65,90,80,98,100];
  return (
    <div className={glassCard("p-4 w-48")} style={{border:"1px solid rgba(139,92,246,0.15)"}}>
      <p className="text-[9.5px] font-mono text-white/30 tracking-widest uppercase mb-1">Hrs Automated</p>
      <div className="flex items-baseline gap-1.5 mb-3">
        <span className="text-[30px] font-black leading-none text-white tracking-tight">2,840</span>
        <div>
          <p className="text-[9.5px] text-emerald-400 font-bold">+24%</p>
          <p className="text-[8.5px] text-white/25">/month</p>
        </div>
      </div>
      <div className="flex gap-[2.5px] items-end h-9">
        {bars.map((h,i)=>(
          <div key={i} className="flex-1 rounded-sm" style={{
            height:`${h}%`,
            background: i>=10?'rgba(59,130,246,0.9)':i>=8?'rgba(59,130,246,0.5)':'rgba(255,255,255,0.07)'
          }}/>
        ))}
      </div>
    </div>
  );
}

function PipelineCard() {
  const stages=[{l:"Sourced",v:1247,pct:100},{l:"Qualified",v:312,pct:25},{l:"Closed",v:24,pct:2}];
  return (
    <div className={glassCard("p-4 w-52")} style={{border:"1px solid rgba(59,130,246,0.12)"}}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] font-mono text-white/30 tracking-widest uppercase">Lead Pipeline</span>
        <TrendingUp className="w-3 h-3 text-emerald-400"/>
      </div>
      <div className="space-y-2.5">
        {stages.map(s=>(
          <div key={s.l}>
            <div className="flex justify-between mb-1">
              <span className="text-[10.5px] text-white/50">{s.l}</span>
              <span className="text-[10px] font-mono text-white/35">{s.v.toLocaleString()}</span>
            </div>
            <div className="h-1 bg-white/[0.05] rounded-full overflow-hidden">
              <motion.div initial={{width:0}} animate={{width:`${s.pct}%`}}
                transition={{duration:1.4,delay:0.3,ease:"easeOut"}}
                className="h-full rounded-full bg-gradient-to-r from-blue-500 to-violet-500"/>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusCard() {
  const checks=[
    {label:"WhatsApp API", ok:true},
    {label:"CRM Integration", ok:true},
    {label:"AI Engine", ok:true},
  ];
  return (
    <div className={glassCard("p-3.5 w-44")} style={{border:"1px solid rgba(52,211,153,0.12)"}}>
      <div className="flex items-center gap-1.5 mb-2.5">
        <Shield className="w-3 h-3 text-emerald-400"/>
        <span className="text-[9.5px] font-mono text-white/30 tracking-widest uppercase">System OK</span>
      </div>
      {checks.map(c=>(
        <div key={c.label} className="flex items-center gap-2 py-1">
          <div className={`w-3.5 h-3.5 rounded-full flex items-center justify-center ${c.ok?'bg-emerald-500/20':'bg-red-500/20'}`}>
            <Check className={`w-2 h-2 ${c.ok?'text-emerald-400':'text-red-400'}`} strokeWidth={3}/>
          </div>
          <span className="text-[10.5px] text-white/45">{c.label}</span>
        </div>
      ))}
      <div className="mt-2.5 pt-2 border-t border-white/[0.05]">
        <span className="text-[9px] font-mono text-emerald-400/70">All systems operational</span>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   WORKFLOW PIPELINE BAR
────────────────────────────────────────────────────────────── */
function WorkflowBar() {
  const steps=["SCAN","QUALIFY","ENRICH","NOTIFY"];
  return (
    <div className={glassCard("p-4 flex items-center gap-0")} style={{
      minWidth:'280px',border:'1px solid rgba(59,130,246,0.12)'
    }}>
      {steps.map((s,i)=>(
        <React.Fragment key={s}>
          <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[8px] font-bold ${i===0?'bg-blue-500 text-white':'bg-white/[0.06] text-white/35'}`}>{i+1}</div>
            <span className="text-[7.5px] font-mono text-white/30 tracking-wider uppercase">{s}</span>
          </div>
          {i<steps.length-1&&(
            <div className="flex-1 mx-1.5 h-px bg-white/[0.06] relative overflow-hidden" style={{minWidth:'24px'}}>
              <div className="absolute top-0 left-0 h-full w-6" style={{
                background:'linear-gradient(90deg,transparent,rgba(96,165,250,0.7),transparent)',
                animation:`data-flow ${2.2+i*0.4}s linear ${i*0.35}s infinite`
              }}/>
            </div>
          )}
        </React.Fragment>
      ))}
      <div className="ml-3 pl-3 border-l border-white/[0.05] flex-shrink-0">
        <p className="text-[9.5px] font-mono text-blue-400/80">LIVE</p>
        <p className="text-[8.5px] text-white/25">24 leads/hr</p>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   FLOATING GLOW ORBS
────────────────────────────────────────────────────────────── */
const ORBS = [
  {s:6, top:"8%",  left:"12%", c:"rgba(96,165,250,0.8)",  delay:0},
  {s:4, top:"15%", right:"10%",c:"rgba(167,139,250,0.7)", delay:1.2},
  {s:5, top:"60%", left:"6%",  c:"rgba(52,211,153,0.7)",  delay:2.1},
  {s:3, top:"75%", right:"14%",c:"rgba(96,165,250,0.6)",  delay:0.8},
  {s:7, top:"40%", left:"3%",  c:"rgba(167,139,250,0.5)", delay:1.6},
  {s:4, top:"85%", left:"45%", c:"rgba(96,165,250,0.7)",  delay:2.8},
  {s:3, top:"22%", left:"52%", c:"rgba(52,211,153,0.6)",  delay:3.2},
];

/* ──────────────────────────────────────────────────────────────
   FULL 3D AI VISUAL COMPOSITION
────────────────────────────────────────────────────────────── */
function AIVisual({ mouseX: mx, mouseY: my }) {
  const springCfg = { stiffness: 45, damping: 18 };
  const sx = useSpring(mx, springCfg);
  const sy = useSpring(my, springCfg);

  const rotX = useTransform(sy, [-0.5, 0.5], [4, -4]);
  const rotY = useTransform(sx, [-0.5, 0.5], [-6, 6]);

  const l1x = useTransform(sx, [-0.5,0.5],[-6,6]);
  const l1y = useTransform(sy, [-0.5,0.5],[-6,6]);
  const l2x = useTransform(sx, [-0.5,0.5],[-14,14]);
  const l2y = useTransform(sy, [-0.5,0.5],[-14,14]);
  const l3x = useTransform(sx, [-0.5,0.5],[-22,22]);
  const l3y = useTransform(sy, [-0.5,0.5],[-22,22]);
  const l4x = useTransform(sx, [-0.5,0.5],[-30,30]);
  const l4y = useTransform(sy, [-0.5,0.5],[-30,30]);

  return (
    <motion.div
      style={{ rotateX:rotX, rotateY:rotY, transformPerspective:1200 }}
      className="relative w-full h-[520px]"
    >
      {/* Background aurora */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[-8%] left-[18%] w-[380px] h-[380px] aurora-blue animate-aurora-1 opacity-65"/>
        <div className="absolute bottom-[-5%] right-[8%] w-[300px] h-[300px] aurora-violet animate-aurora-2 opacity-55"/>
      </div>

      {/* Layer 1 — Neural network */}
      <motion.div style={{x:l1x,y:l1y}} className="absolute inset-0">
        <NeuralNet/>
      </motion.div>

      {/* Layer 2 — Glow orbs */}
      <motion.div style={{x:l2x,y:l2y}} className="absolute inset-0 pointer-events-none">
        {ORBS.map((o,i)=>{
          const {s,c,delay,...pos}=o;
          return (
            <div key={i} className="absolute rounded-full" style={{
              width:s*2, height:s*2, ...pos,
              background:`radial-gradient(circle,${c} 0%,transparent 70%)`,
              filter:`blur(${s<5?1:2}px)`,
              animation:`glow-orb ${3+i*0.6}s ease-in-out ${delay}s infinite`,
              boxShadow:`0 0 ${s*2}px ${c}`,
            }}/>
          );
        })}
      </motion.div>

      {/* Layer 2 — Sphere (same depth as orbs) */}
      <motion.div
        style={{x:l2x, y:l2y, position:"absolute", top:"50%", left:"50%"}}
        initial={{opacity:0,scale:0.8}} animate={{opacity:1,scale:1}}
        transition={{duration:1.2,ease:[0.22,1,0.36,1]}}
      >
        <div style={{transform:"translate(-50%,-60%)"}}>
          <AISphere/>
        </div>
      </motion.div>

      {/* Layer 3 — Top-left card: Automation toggles */}
      <motion.div
        style={{x:l3x, y:l3y, position:"absolute", top:"4%", left:"1%"}}
        className="animate-float"
        initial={{opacity:0,x:-18}} animate={{opacity:1,x:0}}
        transition={{duration:0.9,delay:0.7,ease:[0.22,1,0.36,1]}}
      >
        <AutomationCard/>
      </motion.div>

      {/* Layer 3 — Top-right card: Metrics chart */}
      <motion.div
        style={{x:l3x, y:l3y, position:"absolute", top:"2%", right:"1%"}}
        className="animate-float-r"
        initial={{opacity:0,x:18}} animate={{opacity:1,x:0}}
        transition={{duration:0.9,delay:0.9,ease:[0.22,1,0.36,1]}}
      >
        <MetricsCard/>
      </motion.div>

      {/* Layer 4 — Bottom-left card: Pipeline */}
      <motion.div
        style={{x:l4x, y:l4y, position:"absolute", bottom:"20%", left:"1%"}}
        className="animate-float"
        initial={{opacity:0,y:18}} animate={{opacity:1,y:0}}
        transition={{duration:0.9,delay:1.1,ease:[0.22,1,0.36,1]}}
      >
        <PipelineCard/>
      </motion.div>

      {/* Layer 4 — Bottom-right card: Status */}
      <motion.div
        style={{x:l4x, y:l4y, position:"absolute", bottom:"20%", right:"1%"}}
        className="animate-float-r"
        initial={{opacity:0,y:18}} animate={{opacity:1,y:0}}
        transition={{duration:0.9,delay:1.3,ease:[0.22,1,0.36,1]}}
      >
        <StatusCard/>
      </motion.div>

      {/* Layer 4 — Bottom center: Workflow bar */}
      <motion.div
        style={{x:l4x, y:l4y, position:"absolute", bottom:"0%", left:"50%", transform:"translateX(-50%)"}}
        initial={{opacity:0,y:14}} animate={{opacity:1,y:0}}
        transition={{duration:0.9,delay:1.5,ease:[0.22,1,0.36,1]}}
      >
        <WorkflowBar/>
      </motion.div>
    </motion.div>
  );
}

/* ──────────────────────────────────────────────────────────────
   MAIN HERO
────────────────────────────────────────────────────────────── */
const stagger = { hidden:{}, show:{ transition:{ staggerChildren:0.1 }}};
const item = {
  hidden:{ opacity:0, y:22, filter:"blur(4px)" },
  show:{ opacity:1, y:0, filter:"blur(0px)", transition:{ duration:0.7, ease:[0.22,1,0.36,1] }},
};

export default function Hero() {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const sectionRef = useRef(null);

  const onMouseMove = useCallback((e) => {
    if (!sectionRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    mouseX.set((e.clientX - rect.left) / rect.width - 0.5);
    mouseY.set((e.clientY - rect.top) / rect.height - 0.5);
  }, [mouseX, mouseY]);

  const onMouseLeave = useCallback(() => {
    mouseX.set(0); mouseY.set(0);
  }, [mouseX, mouseY]);

  return (
    <section
      data-testid="hero-section"
      id="hero"
      ref={sectionRef}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      className="relative min-h-screen overflow-hidden bg-[#07070A] flex items-center"
    >
      {/* Global aurora bg */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-80 left-0 w-[700px] h-[700px] aurora-blue animate-aurora-1 opacity-50"/>
        <div className="absolute -bottom-40 right-0 w-[600px] h-[600px] aurora-violet animate-aurora-2 opacity-45"/>
        <div className="absolute inset-0 dot-grid opacity-[0.18]"/>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#07070A]"/>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 pt-28 pb-16 lg:pt-32 lg:pb-20 min-h-screen flex items-center">
        <div className="flex flex-col lg:flex-row items-center w-full gap-10 lg:gap-6">

          {/* ── LEFT: Text ─────────────────────── */}
          <motion.div
            variants={stagger} initial="hidden" animate="show"
            className="lg:w-[46%] text-center lg:text-left flex-shrink-0"
          >
            {/* Status badge */}
            <motion.div variants={item} className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08] mb-6 backdrop-blur-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400" style={{boxShadow:"0 0 6px rgba(96,165,250,0.9)"}}/>
              <span className="text-[10.5px] font-mono tracking-[0.18em] text-white/45 uppercase">AI Automation Studio</span>
            </motion.div>

            {/* Headline */}
            <motion.h1 variants={item}
              className="font-black tracking-[-0.045em] text-white leading-[0.95] mb-6"
              style={{fontSize:"clamp(2.3rem,4.6vw,3.9rem)"}}>
              <span className="block text-white/88">AI Automation</span>
              <span className="block text-white/88">Systems Built For</span>
              <span className="block gradient-text">Modern Business.</span>
            </motion.h1>

            {/* Sub */}
            <motion.p variants={item}
              className="text-[14.5px] sm:text-[15.5px] text-white/42 leading-[1.7] tracking-[-0.01em] mb-8 max-w-[460px] mx-auto lg:mx-0">
              We build intelligent automation systems, AI agents, WhatsApp workflows,
              and business operations that save time, reduce manual work, and scale
              your company faster.
            </motion.p>

            {/* CTAs */}
            <motion.div variants={item} className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start mb-8">
              <a href="https://wa.me/918248195127?text=Hi%20AIFlow%20Labs!%20I%20want%20to%20book%20a%20free%20strategy%20call."
                target="_blank" rel="noopener noreferrer"
                data-testid="hero-book-call-btn"
                className="btn-shine group inline-flex items-center justify-center gap-2 px-7 py-[14px] rounded-xl bg-gradient-to-b from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500 text-white font-semibold text-[14px] tracking-[-0.01em] transition-all duration-200 shadow-[0_1px_0_rgba(255,255,255,0.12)_inset,0_8px_32px_rgba(59,130,246,0.28)] hover:shadow-[0_1px_0_rgba(255,255,255,0.15)_inset,0_12px_40px_rgba(59,130,246,0.4)]">
                Book Free Strategy Call
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform"/>
              </a>
              <a href="#services"
                data-testid="hero-view-services-btn"
                className="inline-flex items-center justify-center gap-2 px-7 py-[14px] rounded-xl border border-white/[0.09] bg-white/[0.03] hover:bg-white/[0.06] text-white/75 hover:text-white font-semibold text-[14px] tracking-[-0.01em] transition-all duration-200 backdrop-blur-sm">
                View Services
              </a>
            </motion.div>

            {/* Mini stats */}
            <motion.div variants={item} className="flex items-center gap-6 justify-center lg:justify-start">
              {[{v:"150+",l:"Automations"},{v:"10K+",l:"Hours Saved"},{v:"90%",l:"Faster Support"}].map((s,i)=>(
                <React.Fragment key={s.v}>
                  {i>0 && <div className="w-px h-8 bg-white/[0.07]"/>}
                  <div>
                    <p className="text-[18px] font-black text-white tracking-tight leading-none">{s.v}</p>
                    <p className="text-[10.5px] text-white/30 mt-0.5">{s.l}</p>
                  </div>
                </React.Fragment>
              ))}
            </motion.div>

            {/* Trusted by */}
            <motion.div variants={item} className="mt-8 pt-6 border-t border-white/[0.05]">
              <p className="text-[9.5px] font-mono text-white/18 tracking-[0.22em] uppercase mb-3">Trusted by teams at</p>
              <div className="flex flex-wrap gap-5 justify-center lg:justify-start">
                {["Acme Corp","Nimbus AI","Flux Systems","Orion Labs","Vertex"].map(n=>(
                  <span key={n} className="text-[11.5px] text-white/16 font-semibold tracking-wide hover:text-white/38 transition-colors duration-300 cursor-default">{n}</span>
                ))}
              </div>
            </motion.div>
          </motion.div>

          {/* ── RIGHT: 3D Visual ──────────────── */}
          <div className="lg:w-[54%] w-full max-w-[640px] mx-auto lg:mx-0 relative hidden lg:block">
            <AIVisual mouseX={mouseX} mouseY={mouseY}/>
          </div>

          {/* Mobile visual (simplified) */}
          <div className="lg:hidden w-full max-w-sm mx-auto">
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute -inset-12 rounded-full aurora-blue opacity-60 blur-3xl"/>
                <AISphere/>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-7 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 opacity-20 hidden lg:flex">
        <span className="text-[8.5px] font-mono tracking-[0.28em] uppercase text-white/60">scroll</span>
        <motion.div animate={{y:[0,7,0]}} transition={{repeat:Infinity,duration:1.9,ease:"easeInOut"}}
          className="w-px h-7 bg-gradient-to-b from-white/60 to-transparent"/>
      </div>
    </section>
  );
}
